export function toNumber(value) {
  return Number(value || 0);
}

export function normalizeTrafficBucket(bucket) {
  if (!bucket) {
    return {
      count: 0,
      segments: 0,
      spend: 0,
      inbound: { count: 0, segments: 0, spend: 0 },
      outbound: { count: 0, segments: 0, spend: 0 },
    };
  }
  const normalizeDirection = (direction) => ({
    count: toNumber(direction && direction.count),
    segments: toNumber(direction && direction.segments),
    spend: toNumber(direction && direction.spend),
  });
  return {
    count: toNumber(bucket.count),
    segments: toNumber(bucket.segments),
    spend: toNumber(bucket.spend),
    inbound: normalizeDirection(bucket.inbound || {}),
    outbound: normalizeDirection(bucket.outbound || {}),
  };
}

export function normalizeVoiceBucket(bucket) {
  if (!bucket) {
    return {
      count: 0,
      minutes: 0,
      spend: 0,
      inbound: { count: 0, minutes: 0, spend: 0 },
      outbound: { count: 0, minutes: 0, spend: 0 },
    };
  }
  const normalizeDirection = (direction) => ({
    count: toNumber(direction && direction.count),
    minutes: toNumber(direction && direction.minutes),
    spend: toNumber(direction && direction.spend),
  });
  return {
    count: toNumber(bucket.count),
    minutes: toNumber(bucket.minutes),
    spend: toNumber(bucket.spend),
    inbound: normalizeDirection(bucket.inbound || {}),
    outbound: normalizeDirection(bucket.outbound || {}),
  };
}

export function normalizeVideoBucket(bucket) {
  if (!bucket) {
    return {
      rooms: 0,
      participantMinutes: 0,
      spend: 0,
      inbound: { rooms: 0, participantMinutes: 0, spend: 0 },
      outbound: { rooms: 0, participantMinutes: 0, spend: 0 },
    };
  }
  const normalizeDirection = (direction) => ({
    rooms: toNumber(direction && direction.rooms),
    participantMinutes: toNumber(direction && direction.participantMinutes),
    spend: toNumber(direction && direction.spend),
  });
  return {
    rooms: toNumber(bucket.rooms),
    participantMinutes: toNumber(bucket.participantMinutes),
    spend: toNumber(bucket.spend),
    inbound: normalizeDirection(bucket.inbound || {}),
    outbound: normalizeDirection(bucket.outbound || {}),
  };
}

export function parseBillingTotals(metrics = {}) {
  const sms = normalizeTrafficBucket(metrics.sms);
  const mms = normalizeTrafficBucket(metrics.mms);
  const voice = normalizeVoiceBucket(metrics.voice);
  const video = normalizeVideoBucket(metrics.video);
  const carrierCharges = toNumber(metrics.carrierCharges);
  const a2pRegistrationCharges = toNumber(metrics.a2pRegistrationCharges);

  const serviceSpend =
    sms.spend +
    mms.spend +
    voice.spend +
    video.spend +
    carrierCharges +
    a2pRegistrationCharges;

  return {
    sms,
    mms,
    voice,
    video,
    carrierCharges,
    a2pRegistrationCharges,
    totalSpend: toNumber(metrics.totalSpend) || serviceSpend,
    serviceSpend,
  };
}

export function normalizeDailyBillingEntry(entry) {
  const parsed = parseBillingTotals({
    sms: entry && entry.sms,
    mms: entry && entry.mms,
    voice: entry && entry.voice,
    video: entry && entry.video,
    carrierCharges: entry && entry.carrierCharges,
    a2pRegistrationCharges: entry && entry.a2pRegistrationCharges,
    totalSpend: entry && entry.totalSpend,
  });
  return parsed;
}

export function getInvoiceVoiceBreakdown(invoice = {}, costBreakdown = {}) {
  const voice = costBreakdown.voice || {};
  return {
    calls: toNumber(voice.calls || invoice.voice_calls || invoice.voiceCalls),
    minutes: toNumber(voice.minutes || invoice.voice_minutes || invoice.voiceMinutes),
    cost: toNumber(voice.cost || invoice.voice_cost || invoice.voiceCost),
    inbound: {
      calls: toNumber(
        (voice.inbound && voice.inbound.calls) ||
          invoice.voice_inbound_calls ||
          invoice.voiceInboundCalls
      ),
      minutes: toNumber(
        (voice.inbound && voice.inbound.minutes) ||
          invoice.voice_inbound_minutes ||
          invoice.voiceInboundMinutes
      ),
      cost: toNumber(
        (voice.inbound && voice.inbound.cost) ||
          invoice.voice_inbound_cost ||
          invoice.voiceInboundCost
      ),
    },
    outbound: {
      calls: toNumber(
        (voice.outbound && voice.outbound.calls) ||
          invoice.voice_outbound_calls ||
          invoice.voiceOutboundCalls
      ),
      minutes: toNumber(
        (voice.outbound && voice.outbound.minutes) ||
          invoice.voice_outbound_minutes ||
          invoice.voiceOutboundMinutes
      ),
      cost: toNumber(
        (voice.outbound && voice.outbound.cost) ||
          invoice.voice_outbound_cost ||
          invoice.voiceOutboundCost
      ),
    },
  };
}

export function getInvoiceServiceSpend(invoice = {}) {
  const invoiceData = invoice.invoice_data || invoice.invoiceData || {};
  const lineItems = invoiceData.lineItems || [];
  let smsSpend = 0;
  let mmsSpend = 0;

  lineItems.forEach((item) => {
    const type = String(item.carrierType || item.messageType || '').toUpperCase();
    const total = toNumber(item.total);
    if (type === 'SMS') {
      smsSpend += total;
    } else if (type === 'MMS') {
      mmsSpend += total;
    }
  });

  if (smsSpend === 0 && mmsSpend === 0) {
    (invoiceData.messages || []).forEach((message) => {
      const type = String(message.carrier || 'SMS').toUpperCase();
      const price = toNumber(message.price);
      if (type === 'SMS') {
        smsSpend += price;
      } else if (type === 'MMS') {
        mmsSpend += price;
      }
    });
  }

  const voiceSpend = toNumber(invoice.voice_cost || invoice.voiceSpend || invoice.voiceCost);
  const videoSpend = toNumber(invoice.video_cost || invoice.videoSpend || invoice.videoCost);
  const carrierCharges = toNumber(
    invoice.carrier_charges || invoice.carrierCharges
  );
  const a2pRegistrationCharges = toNumber(
    invoice.a2p_registration_charges || invoice.a2pRegistrationCharges
  );
  const totalSpend = toNumber(
    invoice.total_monthly_spend || invoice.totalMonthlySpend || invoice.totalSpend
  );

  const apiSmsSpend = invoice.smsSpend;
  const apiMmsSpend = invoice.mmsSpend;

  return {
    smsSpend: apiSmsSpend != null ? toNumber(apiSmsSpend) : smsSpend,
    mmsSpend: apiMmsSpend != null ? toNumber(apiMmsSpend) : mmsSpend,
    voiceSpend,
    videoSpend,
    carrierCharges,
    a2pRegistrationCharges,
    totalSpend,
    hasVoiceVideo: voiceSpend > 0 || videoSpend > 0,
  };
}

export function getInvoiceVideoBreakdown(invoice = {}, costBreakdown = {}) {
  const video = costBreakdown.video || {};
  return {
    rooms: toNumber(video.rooms || invoice.video_rooms || invoice.videoRooms),
    participantMinutes: toNumber(
      video.participantMinutes ||
        invoice.video_participant_minutes ||
        invoice.videoParticipantMinutes
    ),
    cost: toNumber(video.cost || invoice.video_cost || invoice.videoCost),
    inbound: {
      rooms: toNumber(
        (video.inbound && video.inbound.rooms) ||
          invoice.video_inbound_rooms ||
          invoice.videoInboundRooms
      ),
      participantMinutes: toNumber(
        (video.inbound && video.inbound.participantMinutes) ||
          invoice.video_inbound_participant_minutes ||
          invoice.videoInboundParticipantMinutes
      ),
      cost: toNumber(
        (video.inbound && video.inbound.cost) ||
          invoice.video_inbound_cost ||
          invoice.videoInboundCost
      ),
    },
    outbound: {
      rooms: toNumber(
        (video.outbound && video.outbound.rooms) ||
          invoice.video_outbound_rooms ||
          invoice.videoOutboundRooms
      ),
      participantMinutes: toNumber(
        (video.outbound && video.outbound.participantMinutes) ||
          invoice.video_outbound_participant_minutes ||
          invoice.videoOutboundParticipantMinutes
      ),
      cost: toNumber(
        (video.outbound && video.outbound.cost) ||
          invoice.video_outbound_cost ||
          invoice.videoOutboundCost
      ),
    },
  };
}



// WEBPACK FOOTER //
// ./src/components/billing/billingMetricsUtils.js